import { ApiError } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { SearchInput } from '@/components/custom/search-input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { customersApi, customerTeamApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { localTodayIso } from '@/lib/utils';
import { Section } from './section';

/**
 * Customer team memberships for a single user — rendered on the user
 * detail page. By default shows memberships in effect today; the "Show
 * ended" toggle widens the window to a fixed [1900-01-01, 2999-12-31] so
 * the server returns every row for the user (the date-range overlap
 * filter on `findForUserInRange` does the date filtering).
 *
 * The search box is a client-side substring filter over customer name
 * and service role name — the dataset is bounded by one user's
 * memberships (~tens at most), so an in-memory filter is sufficient and
 * keeps the typing experience instant. The endpoint does not paginate
 * today; if a user is ever on more than a screen of customers, swap
 * search and paging to the server in lockstep.
 *
 * **Error surfacing (G15).** Two queries fire — memberships (which carry
 * `serviceRoleName` resolved server-side) and customer names (`findByIds`).
 * Memberships failing means there's nothing to show; that error replaces
 * the table. The customer-name query failing degrades the table (no
 * customer names), so we render an inline banner so the user knows the
 * missing names are a load failure, not a genuine empty value. The
 * customer cell distinguishes "still loading" (`…`) from "settled but
 * missing" (`Unknown customer`) — the latter only after the lookup
 * resolves. A `null` `serviceRoleName` on a loaded row is a real gap
 * (deleted role) and renders as "Unknown role".
 *
 * Visibility (rendering this component at all) is the parent's call.
 */
interface Props {
  userId: string;
}

// Wide window for "show all" — the server filters by date-range overlap,
// so this captures every membership the user has ever had. The endpoint
// regex requires YYYY-MM-DD; year 2999 stays safely under Postgres `date`
// limits.
const WIDE_FROM = '1900-01-01';
const WIDE_TO = '2999-12-31';

export function UserCustomerTeamSection({ userId }: Props) {
  const [showEnded, setShowEnded] = useState(false);
  const [search, setSearch] = useState('');

  const today = localTodayIso();
  const fromDate = showEnded ? WIDE_FROM : today;
  const toDate = showEnded ? WIDE_TO : today;

  const memberships = useQuery({
    queryKey: queryKeys.customerTeam.forUser({
      teamUserId: userId,
      fromDate,
      toDate,
      withLabels: true,
    }),
    queryFn: () => customerTeamApi.listForUserWithLabels({ teamUserId: userId, fromDate, toDate }),
  });
  const rows = memberships.data?.data ?? [];

  // Unique customer ids in the result — drives a single batched name
  // lookup. Sort so the resulting query key is stable across re-renders
  // when the row order isn't (the API sorts by customerId, but defensive).
  const customerIds = useMemo(() => {
    const ids = Array.from(new Set(rows.map((r) => r.customerId)));
    ids.sort();
    return ids;
  }, [rows]);

  const customersQuery = useQuery({
    queryKey: queryKeys.customers.byIds(customerIds),
    queryFn: () => customersApi.findByIds(customerIds),
    enabled: customerIds.length > 0,
  });
  const customerById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of customersQuery.data?.data ?? []) map.set(c.id, c.name);
    return map;
  }, [customersQuery.data]);

  // Case-insensitive substring filter on customer name OR service role
  // name. Filtering in memory is fine here — the parent endpoint already
  // caps the result to a single user's memberships (~tens at most). The
  // service-role name rides on the row; the customer-name map is resolved
  // by the time we render rows.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((m) => {
      const cn = (customerById.get(m.customerId) ?? '').toLowerCase();
      const rn = (m.serviceRoleName ?? '').toLowerCase();
      return cn.includes(q) || rn.includes(q);
    });
  }, [rows, search, customerById]);

  const isFiltering = search.trim().length > 0;
  const countBadge = isFiltering ? `${filteredRows.length} / ${rows.length}` : rows.length;

  // "Settled" = the query has resolved (success or error); during this
  // state, a missing id in the lookup map indicates a real gap, not
  // mid-flight loading. Used by the per-cell fallback to choose between
  // "…" (loading) and "Unknown ..." (settled missing).
  const customersSettled = customerIds.length === 0 || customersQuery.status !== 'pending';

  return (
    <Section
      title="Customers"
      description="Customer teams this user is part of, with their service role and engagement rate."
      count={countBadge}
      headerRight={
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            placeholder="Search customer or role…"
            value={search}
            onValueChange={setSearch}
            className="w-60"
            inputClassName="h-8"
            aria-label="Filter customer memberships"
          />
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              className="size-3.5 rounded border-input"
              checked={showEnded}
              onChange={(e) => setShowEnded(e.target.checked)}
            />
            <span>Show ended</span>
          </label>
        </div>
      }
    >
      {memberships.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : memberships.isError ? (
        <p className="text-sm text-destructive">
          {memberships.error instanceof ApiError
            ? memberships.error.message
            : 'Failed to load customer memberships.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {showEnded
            ? 'No customer team memberships on record.'
            : 'Not on any customer team today.'}
        </p>
      ) : filteredRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No customers match “{search.trim()}”.</p>
      ) : (
        <>
          <LabelLoadBanner
            customersError={customersQuery.error}
            customersFailed={customersQuery.isError}
          />
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Service role</TableHead>
                  <TableHead>Effective</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((m) => {
                  const ended = m.effectiveTo != null && m.effectiveTo <= today;
                  return (
                    <TableRow key={m.id}>
                      <TableCell>
                        <CustomerCell
                          customerId={m.customerId}
                          name={customerById.get(m.customerId)}
                          settled={customersSettled}
                          teamLead={m.teamLead}
                        />
                      </TableCell>
                      <TableCell className="text-sm">
                        <RoleCell name={m.serviceRoleName} />
                      </TableCell>
                      <TableCell className="text-xs">
                        {m.effectiveFrom} →{' '}
                        <span className={ended ? 'text-muted-foreground' : 'font-medium'}>
                          {m.effectiveTo ?? 'Current'}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </Section>
  );
}

/**
 * Inline banner that fires only when the customer-name lookup failed AND
 * the membership query succeeded. We don't show one when memberships
 * itself failed — the table is replaced by an error message in that case,
 * so a banner on top would be noise. (Service-role names ride on the
 * membership rows, so they share the membership query's fate.)
 *
 * Per G15: a silent placeholder ("…") next to a half-broken table leaves
 * the user unable to distinguish "no customer" from "the lookup 500'd".
 * The banner names the failure so a refresh is an obvious next step.
 */
function LabelLoadBanner({
  customersFailed,
  customersError,
}: {
  customersFailed: boolean;
  customersError: unknown;
}) {
  if (!customersFailed) return null;
  const detail =
    customersError instanceof ApiError
      ? `customer names (${customersError.message})`
      : 'customer names';
  return (
    <p className="rounded border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
      Couldn't load {detail} — affected cells show “Unknown”. Refresh to retry.
    </p>
  );
}

function CustomerCell({
  customerId,
  name,
  settled,
  teamLead,
}: {
  customerId: string;
  name: string | undefined;
  settled: boolean;
  teamLead: boolean;
}) {
  if (name != null) {
    return (
      <>
        <Link
          to="/customers/$id"
          params={{ id: customerId }}
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          {name}
        </Link>
        {teamLead ? <LeadBadge /> : null}
      </>
    );
  }
  // While the lookup is in flight we show "…" — but as soon as the query
  // has settled with the id still missing, that's a real data gap, not
  // loading. Don't render a link to a customer we can't name; the click
  // would land on a 404.
  return (
    <>
      <span
        className={settled ? 'font-medium text-muted-foreground' : 'text-muted-foreground'}
        title={settled ? `Customer ${customerId} not in lookup response` : undefined}
      >
        {settled ? 'Unknown customer' : '…'}
      </span>
      {teamLead ? <LeadBadge /> : null}
    </>
  );
}

// `serviceRoleName` rides on the loaded membership row, so a null here is a
// real gap (the role was deleted), never a loading state.
function RoleCell({ name }: { name: string | null | undefined }) {
  if (name != null) return <>{name}</>;
  return <span className="text-muted-foreground">Unknown role</span>;
}

function LeadBadge() {
  return (
    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
      Lead
    </span>
  );
}
